/**
 * Optical job contracts shared between the API and the web.
 *
 * One job is one pair of spectacles beneath a sales order. The order stays the only commercial record — prices, tax, payments, invoices — and a job holds no money. `id` is the identity every action names; `reference` (SO-000123-J1) is generated by the server, never by a client, and never changes.
 */

import type {
  FrameMaterial,
  FrameRimType,
  LensMaterial,
  LensType,
  PrescriptionLensSupplyMode,
} from './catalog';
import type { PdType } from './measurements';
import type { PaymentPlanStatus } from './receivables';
import type {
  EyePrescription,
  PaymentMethod,
  PrescriptionSource,
  PrismBase,
  SalesLineWorkflowStatus,
  SalesOrderStatus,
} from './sales';

export const OpticalJobStatus = {
  ORDERED: 'ORDERED',
  SENT_TO_LAB: 'SENT_TO_LAB',
  IN_LAB: 'IN_LAB',
  RECEIVED_FROM_LAB: 'RECEIVED_FROM_LAB',
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
} as const;
export type OpticalJobStatus = (typeof OpticalJobStatus)[keyof typeof OpticalJobStatus];

/** CHECKOUT jobs are whole from the moment they commit; LEGACY jobs record only what the pre-job rows they were built from prove. */
export const OpticalJobOrigin = { CHECKOUT: 'CHECKOUT', LEGACY: 'LEGACY' } as const;
export type OpticalJobOrigin = (typeof OpticalJobOrigin)[keyof typeof OpticalJobOrigin];

export const OpticalEye = { RIGHT: 'RIGHT', LEFT: 'LEFT' } as const;
export type OpticalEye = (typeof OpticalEye)[keyof typeof OpticalEye];

/** What wrote a history row: the checkout, a job transition, the backfill, or the legacy line workflow a LEGACY job mirrors. */
export const OpticalJobStatusChangeKind = {
  CREATED: 'CREATED',
  TRANSITION: 'TRANSITION',
  LEGACY_BACKFILL: 'LEGACY_BACKFILL',
  LEGACY_WORKFLOW: 'LEGACY_WORKFLOW',
} as const;
export type OpticalJobStatusChangeKind =
  (typeof OpticalJobStatusChangeKind)[keyof typeof OpticalJobStatusChangeKind];

/** How many pairs one order may carry. A product limit the server enforces, not a database maximum. */
export const MAX_OPTICAL_JOBS_PER_ORDER = 4;

/** The single forward step each status allows. ORDERED → CANCELLED belongs to the dedicated optical cancellation alone. */
export const OPTICAL_JOB_TRANSITIONS: Readonly<
  Record<OpticalJobStatus, readonly OpticalJobStatus[]>
> = {
  ORDERED: ['SENT_TO_LAB', 'CANCELLED'],
  SENT_TO_LAB: ['IN_LAB'],
  IN_LAB: ['RECEIVED_FROM_LAB'],
  RECEIVED_FROM_LAB: ['READY_FOR_COLLECTION'],
  READY_FOR_COLLECTION: ['DELIVERED'],
  DELIVERED: [],
  CANCELLED: [],
};

export function canTransitionOpticalJob(from: OpticalJobStatus, to: OpticalJobStatus): boolean {
  return OPTICAL_JOB_TRANSITIONS[from].includes(to);
}

export const FINAL_OPTICAL_JOB_STATUSES: readonly OpticalJobStatus[] = ['DELIVERED', 'CANCELLED'];

/** Lens designs that need the prescription's addition for their eye and a fitting height. */
export const MULTIFOCAL_LENS_TYPES: readonly LensType[] = ['PROGRESSIVE', 'BIFOCAL', 'OFFICE'];

export function isMultifocalLensType(type: LensType): boolean {
  return MULTIFOCAL_LENS_TYPES.includes(type);
}

/** The approved mapping from the nine legacy line statuses to the seven job statuses. */
export const LEGACY_WORKFLOW_TO_JOB_STATUS: Readonly<
  Record<SalesLineWorkflowStatus, OpticalJobStatus>
> = {
  NOT_STARTED: 'ORDERED',
  WAITING_FOR_LENS: 'ORDERED',
  SENT_TO_LAB: 'SENT_TO_LAB',
  IN_PRODUCTION: 'IN_LAB',
  RECEIVED_FROM_LAB: 'RECEIVED_FROM_LAB',
  QC: 'RECEIVED_FROM_LAB',
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
  CUSTOMER_NOTIFIED: 'READY_FOR_COLLECTION',
  DELIVERED: 'DELIVERED',
};

/** The legacy line status written beside each job status while the legacy columns remain. A cancelled job leaves its lines as they were; the order carries the cancellation. */
export const JOB_STATUS_TO_LEGACY_WORKFLOW: Readonly<
  Record<Exclude<OpticalJobStatus, 'CANCELLED'>, SalesLineWorkflowStatus>
> = {
  ORDERED: 'NOT_STARTED',
  SENT_TO_LAB: 'SENT_TO_LAB',
  IN_LAB: 'IN_PRODUCTION',
  RECEIVED_FROM_LAB: 'RECEIVED_FROM_LAB',
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
  DELIVERED: 'DELIVERED',
};

// ---------------------------------------------------------------------------
// Labs
// ---------------------------------------------------------------------------

export interface OpticalLabSummary {
  id: string;
  name: string;
  isInHouse: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOpticalLabInput {
  name: string;
  isInHouse?: boolean;
}

export interface UpdateOpticalLabInput {
  name?: string;
  isInHouse?: boolean;
  isActive?: boolean;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** The frame as sold. Every field is a snapshot; a LEGACY job may have only the line and variant. */
export interface OpticalJobFrameSnapshot {
  lineId: string;
  variantId: string;
  sku: string | null;
  productName: string | null;
  variantName: string | null;
  brandName: string | null;
  colorName: string | null;
  colorCode: string | null;
  lensWidthMm: number | null;
  bridgeWidthMm: number | null;
  templeLengthMm: number | null;
  rimType: FrameRimType | null;
  material: FrameMaterial | null;
}

/** One eye's lens as sold, with the power range its prescription was checked against. */
export interface OpticalJobLensSnapshot {
  eye: OpticalEye;
  lineId: string;
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  brandName: string | null;
  lensType: LensType;
  material: LensMaterial;
  refractiveIndex: string | null;
  hasAntiReflective: boolean;
  hasBlueLightFilter: boolean;
  isPhotochromic: boolean;
  hasScratchResistant: boolean;
  supplyMode: PrescriptionLensSupplyMode;
  sphereMin: string | null;
  sphereMax: string | null;
  cylinderMin: string | null;
  cylinderMax: string | null;
  additionMin: string | null;
  additionMax: string | null;
  diameterMm: number | null;
}

/** The complete dispensing snapshot a job is made to. Returned only to users who may read optical jobs. */
export interface OpticalJobPrescription {
  id: string;
  /** EXTERNAL_PRESCRIPTION or CLINICAL_PRESCRIPTION for a job taken at the counter; a LEGACY job keeps the source it was recorded with. */
  source: PrescriptionSource;
  /** CLINICAL_PRESCRIPTION only: the exact clinical prescription the snapshot was copied from, and its number. */
  clinicalPrescriptionId: string | null;
  clinicalPrescriptionNumber: string | null;
  right: EyePrescription;
  left: EyePrescription;
  rightPdMm: string | null;
  leftPdMm: string | null;
  totalPdMm: string | null;
  pdType: PdType | null;
  externalReference: string | null;
  prescriberName: string | null;
  /** `YYYY-MM-DD`: the day an external prescription was written, or the day a clinical one was issued. */
  prescribedOn: string | null;
  /** `YYYY-MM-DD`. */
  expiresOn: string | null;
  updatedAt: string;
}

export interface OpticalJobStatusChangeSummary {
  fromStatus: OpticalJobStatus | null;
  toStatus: OpticalJobStatus;
  kind: OpticalJobStatusChangeKind;
  /** The legacy line statuses a LEGACY job's row was mapped from. */
  legacyStatus: string | null;
  changedAt: string;
  changedByName: string | null;
}

export interface OpticalJobDetail {
  id: string;
  /** SO-000123-J1. Generated by the server and never changed. */
  reference: string;
  jobNumber: number;
  orderId: string;
  orderReference: string;
  branchId: string;
  customerId: string | null;
  origin: OpticalJobOrigin;
  status: OpticalJobStatus;
  /** Send it back with the next change; a stale one is refused with 409. */
  version: number;
  lab: { id: string; name: string; isInHouse: boolean; isActive: boolean } | null;
  labReference: string | null;
  labNotes: string | null;
  /** Millimetres to one decimal place. */
  rightFittingHeightMm: string | null;
  leftFittingHeightMm: string | null;
  frame: OpticalJobFrameSnapshot | null;
  lenses: { right: OpticalJobLensSnapshot | null; left: OpticalJobLensSnapshot | null };
  prescription: OpticalJobPrescription | null;
  /** Every order line that belongs to the job. */
  lineIds: string[];
  history: OpticalJobStatusChangeSummary[];
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** One eye as typed. Powers are decimal strings; blank and null both mean no value. */
export interface EyePrescriptionInput {
  sphere?: string | null;
  cylinder?: string | null;
  axis?: number | null;
  add?: string | null;
  prism?: string | null;
  prismBase?: PrismBase | null;
}

/** The sources a job's prescription can name. */
export const OpticalPrescriptionSource = {
  EXTERNAL_PRESCRIPTION: 'EXTERNAL_PRESCRIPTION',
  CLINICAL_PRESCRIPTION: 'CLINICAL_PRESCRIPTION',
} as const;
export type OpticalPrescriptionSource =
  (typeof OpticalPrescriptionSource)[keyof typeof OpticalPrescriptionSource];

/**
 * A whole prescription: a save replaces every field, and a field left out is stored as no value.
 *
 * `CLINICAL_PRESCRIPTION` sends only `clinicalPrescriptionId`; the server copies its values and refuses any sent alongside. `EXTERNAL_PRESCRIPTION` sends the values and never a clinical id.
 */
export interface OpticalPrescriptionInput {
  source: OpticalPrescriptionSource;
  clinicalPrescriptionId?: string;
  right?: EyePrescriptionInput;
  left?: EyePrescriptionInput;
  pdType?: PdType;
  /** Binocular PD, or both monocular PDs — one of the two, never both. */
  totalPdMm?: string | null;
  rightPdMm?: string | null;
  leftPdMm?: string | null;
  externalReference?: string | null;
  prescriberName?: string | null;
  /** `YYYY-MM-DD`. */
  prescribedOn?: string | null;
  /** `YYYY-MM-DD`. */
  expiresOn?: string | null;
  /** External only: the customer's authoritative measurement the PD was taken from unchanged. The server checks it and refuses a mismatch; never sent for a PD typed by hand. */
  sourceMeasurementId?: string | null;
}

/** One pair at checkout. Lines are named by the `lineKey` each carries in the same request. */
export interface OpticalJobInput {
  frameLineKey: string;
  rightLensLineKey: string;
  leftLensLineKey: string;
  prescription: OpticalPrescriptionInput;
  rightFittingHeightMm?: string | null;
  leftFittingHeightMm?: string | null;
  labId?: string | null;
  labNotes?: string | null;
}

/** Changes to an ORDERED job. A field left out is unchanged; null clears it where that is allowed. */
export interface UpdateOpticalJobInput {
  version: number;
  rightFittingHeightMm?: string | null;
  leftFittingHeightMm?: string | null;
  labId?: string | null;
  labNotes?: string | null;
}

export interface ReplaceOpticalJobPrescriptionInput {
  version: number;
  prescription: OpticalPrescriptionInput;
}

export interface OpticalJobTransitionInput {
  version: number;
}

export interface SendOpticalJobToLabInput extends OpticalJobTransitionInput {
  /** Names the lab now when none was chosen while ordering. */
  labId?: string;
  labReference?: string;
}

export interface MarkOpticalJobInLabInput extends OpticalJobTransitionInput {
  labReference?: string;
}

// ---------------------------------------------------------------------------
// Dispensing a clinical prescription
// ---------------------------------------------------------------------------

/**
 * Whether an issued clinical prescription can be used for a new pair, judged on the tenant's business date.
 *
 * The lifecycle states are Phase 1's own: EXPIRED once its expiry date has passed (the expiry day itself still counts), REPLACED when its amendment was completed, VOIDED when voided, DRAFT when never issued. INCOMPLETE is a current prescription a pair cannot be made to: it lacks an eye or a PD.
 */
export const DispensingUsability = {
  USABLE: 'USABLE',
  INCOMPLETE: 'INCOMPLETE',
  EXPIRED: 'EXPIRED',
  REPLACED: 'REPLACED',
  VOIDED: 'VOIDED',
  DRAFT: 'DRAFT',
} as const;
export type DispensingUsability = (typeof DispensingUsability)[keyof typeof DispensingUsability];

/** One entry of a customer's dispensing list: enough to choose, nothing clinical beyond it. */
export interface DispensingPrescriptionSummary {
  clinicalPrescriptionId: string;
  /** RX-000051. */
  prescriptionNumber: string;
  /** `YYYY-MM-DD`, the tenant's business date of issue. */
  issuedOn: string;
  /** `YYYY-MM-DD`, or null when the examiner set none. */
  expiresOn: string | null;
  usability: DispensingUsability;
}

/** The final dispensing values of one issued prescription: the eyes and the PD frozen at issue. Never the examination behind it. */
export interface DispensingPrescription extends DispensingPrescriptionSummary {
  customerId: string;
  right: EyePrescription;
  left: EyePrescription;
  pd: {
    pdType: PdType | null;
    rightPdMm: string | null;
    leftPdMm: string | null;
    totalPdMm: string | null;
  };
}

// ---------------------------------------------------------------------------
// Cancellation before the lab
// ---------------------------------------------------------------------------

/** One refund the cancellation recorded: the whole remainder of one payment, to the tender that paid it. */
export interface OpticalCancellationRefund {
  id: string;
  /** The payment this refund returns. */
  paymentTransactionId: string;
  method: PaymentMethod;
  amountMinor: number;
}

/** A card or transfer refund's reference, where the tender needs one. Never echoed back. */
export interface OpticalCancellationRefundReference {
  paymentTransactionId: string;
  reference: string;
}

/** Cancels an optical order before any of its jobs reaches the lab. The reason stays on the order; it goes into no audit entry, event or log. */
export interface CancelOpticalOrderRequest {
  /** 3–500 characters. */
  reason: string;
  refundReferences?: OpticalCancellationRefundReference[];
}

/**
 * What a cancellation did, in identifiers, references, amounts and statuses — never the reason, a prescription or a payment reference.
 *
 * `balance` is the order's receivable read after the cancellation, inside its transaction: a cancelled optical order owes nothing, so every figure is zero.
 */
export interface OpticalOrderCancellation {
  orderId: string;
  /** SO-000123. */
  orderReference: string;
  status: SalesOrderStatus;
  cancelledAt: string;
  currencyCode: string;
  jobs: { id: string; reference: string; status: OpticalJobStatus }[];
  refunds: OpticalCancellationRefund[];
  paymentPlan: { id: string; reference: string; status: PaymentPlanStatus } | null;
  /** The ORDER_CANCELLATION note offsetting the order's invoice; null when no invoice was issued. */
  creditNote: { id: string; reference: string; totalMinor: number } | null;
  /** How many stock issues were reversed. A made-to-order lens was never issued and needs none. */
  stockReversals: number;
  balance: { netCollectedMinor: number; outstandingMinor: number; refundableExcessMinor: number };
}

// ---------------------------------------------------------------------------
// Tracking: the queue, a customer's history, what a handover waits on, the lab ticket
// ---------------------------------------------------------------------------

/** The order's money as far as handing a pair over is concerned, for a reader who may see the order's money. */
export interface OpticalJobBalance {
  currencyCode: string;
  outstandingMinor: number;
  /** An active payment plan's unsettled instalments cover what is outstanding — the rule the handover itself applies. */
  coveredByPlan: boolean;
}

/** One job in a list: enough to find it and see where it stands. Where the prescription came from, never its values. */
export interface OpticalJobListItem {
  id: string;
  /** SO-000123-J1. */
  reference: string;
  status: OpticalJobStatus;
  origin: OpticalJobOrigin;
  orderId: string;
  orderReference: string;
  /** When the order was taken. */
  orderedAt: string;
  branch: { id: string; code: string; name: string };
  customer: { id: string; displayName: string; customerNumber: string } | null;
  /** The frame as sold; null for a LEGACY job that never recorded one. */
  frame: { productName: string | null; variantName: string | null; sku: string | null } | null;
  prescription: { source: PrescriptionSource; clinicalPrescriptionNumber: string | null } | null;
  lab: { id: string; name: string; isActive: boolean } | null;
  labReference: string | null;
  /** Null for a reader without sales.view: the order's money is theirs to see, not every optical reader's. */
  balance: OpticalJobBalance | null;
  updatedAt: string;
}

/** Filters for a list of jobs. `open` leaves out DELIVERED and CANCELLED; `status` names one status exactly. */
export interface OpticalJobListQuery {
  page?: number;
  pageSize?: number;
  /** A job or order reference, or a customer's name, number or mobile. */
  search?: string;
  status?: OpticalJobStatus;
  open?: boolean;
  labId?: string;
}

/** What handing a job over waits on: the order's position and whether an active plan covers it. */
export interface OpticalJobCollection {
  jobId: string;
  orderId: string;
  orderReference: string;
  branchId: string;
  currencyCode: string;
  /** What the customer owes for goods, after credit notes. */
  netObligationMinor: number;
  netCollectedMinor: number;
  outstandingMinor: number;
  plan: { id: string; reference: string; unsettledMinor: number } | null;
  /** Nothing is outstanding, or an active plan's unsettled instalments cover it: the handover is not refused for money. */
  handoverCovered: boolean;
}

/** One eye's lens on a lab ticket: what to make, never what it cost or the range it was checked against. */
export interface OpticalLabTicketLens {
  sku: string;
  productName: string;
  variantName: string | null;
  brandName: string | null;
  lensType: LensType;
  material: LensMaterial;
  refractiveIndex: string | null;
  hasAntiReflective: boolean;
  hasBlueLightFilter: boolean;
  isPhotochromic: boolean;
  hasScratchResistant: boolean;
  supplyMode: PrescriptionLensSupplyMode;
  diameterMm: number | null;
}

/**
 * A job's lab ticket: manufacturing information and nothing else.
 *
 * No price, payment, clinical note, examination value beyond the final prescription, face or recommendation data. The PD is exactly the form the job holds: a binocular PD stays binocular and a monocular pair stays monocular — nothing is derived or converted.
 */
export interface OpticalLabTicket {
  jobId: string;
  jobReference: string;
  orderReference: string;
  status: OpticalJobStatus;
  origin: OpticalJobOrigin;
  branchName: string;
  orderedAt: string;
  customer: { displayName: string; customerNumber: string } | null;
  frame: {
    sku: string | null;
    productName: string | null;
    variantName: string | null;
    brandName: string | null;
    colorName: string | null;
    lensWidthMm: number | null;
    bridgeWidthMm: number | null;
    templeLengthMm: number | null;
    rimType: FrameRimType | null;
    material: FrameMaterial | null;
  } | null;
  lenses: { right: OpticalLabTicketLens | null; left: OpticalLabTicketLens | null };
  prescription: {
    source: PrescriptionSource;
    clinicalPrescriptionNumber: string | null;
    externalReference: string | null;
    right: EyePrescription;
    left: EyePrescription;
    pdType: PdType | null;
    rightPdMm: string | null;
    leftPdMm: string | null;
    totalPdMm: string | null;
  } | null;
  rightFittingHeightMm: string | null;
  leftFittingHeightMm: string | null;
  lab: { name: string; isInHouse: boolean } | null;
  labReference: string | null;
  labNotes: string | null;
  /** When the server answered: the moment this copy was produced. */
  printedAt: string;
}
